import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  Divider,
  FormGroup,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { 
  fetchUserMedications, 
  fetchMedicationById, 
  createMedication, 
  updateMedication, 
  deleteMedication 
} from '../services/healinApi';

const MedicationTest = () => {
  const [userId, setUserId] = useState('');
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedMedication, setSelectedMedication] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  
  // Form state
  const [formData, setFormData] = useState({
    medicationName: '',
    dosage: '',
    posology: '',
    startDate: '',
    endDate: '',
    type: 'prescription', // default
    source: 'manual'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      medicationName: '',
      dosage: '',
      posology: '',
      startDate: '',
      endDate: '',
      type: 'prescription',
      source: 'manual'
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEditDialog = (medication) => {
    setSelectedMedication(medication);
    setFormData({
      medicationName: medication.medicationName || '',
      dosage: medication.dosage || '',
      posology: medication.posology || '',
      startDate: medication.startDate || '',
      endDate: medication.endDate || '',
      type: medication.type || 'prescription',
      source: medication.source || 'manual'
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedMedication(null);
  };

  const handleSubmit = async () => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (dialogMode === 'create') {
        const newMedication = await createMedication({
          userId,
          ...formData
        });
        setSuccess('Medication created successfully');
        await fetchMedications();
      } else if (dialogMode === 'edit') {
        const updatedMedication = await updateMedication({
          userId,
          medicationId: selectedMedication.medicationId,
          ...formData
        });
        setSuccess('Medication updated successfully');
        await fetchMedications();
      }
      closeDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (medicationId) => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this medication?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deleteMedication(userId, medicationId);
      setSuccess('Medication deleted successfully');
      await fetchMedications();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedications = async () => {
    if (!userId) {
      setError('Please enter a User ID');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await fetchUserMedications(userId);
      setMedications(data);
    } catch (err) {
      console.error('Error fetching medications:', err);
      setError(err.message);
      setMedications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (medicationId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await fetchMedicationById(userId, medicationId);
      alert(JSON.stringify(data, null, 2));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Medication API Test
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Test the connection to Healin Medication API.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              variant="outlined"
              placeholder="Enter User ID"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="contained" 
              onClick={fetchMedications} 
              disabled={loading || !userId.trim()}
              sx={{ mr: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Fetch Medications'}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              disabled={!userId.trim()}
            >
              Add New
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Paper>

      {medications.length > 0 ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Medications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {medications.length} medication(s) for user {userId}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            {medications.map((medication, index) => (
              <Grid item xs={12} sm={6} md={4} key={medication.medicationId || index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom noWrap>
                      {medication.medicationName || 'Unnamed Medication'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>ID:</strong> {medication.medicationId}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Dosage:</strong> {medication.dosage || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Posology:</strong> {medication.posology || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Type:</strong> {medication.type || 'Not specified'}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button 
                        size="small" 
                        onClick={() => handleViewDetails(medication.medicationId)}
                      >
                        Details
                      </Button>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(medication)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(medication.medicationId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : userId && !loading ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No medications found for this user
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{ mt: 2 }}
          >
            Add Medication
          </Button>
        </Paper>
      ) : null}

      {/* Medication Form Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Medication' : 'Edit Medication'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medication Name"
                name="medicationName"
                value={formData.medicationName}
                onChange={handleChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dosage"
                name="dosage"
                value={formData.dosage}
                onChange={handleChange}
                margin="normal"
                placeholder="e.g., 20mg"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label="Type"
                >
                  <MenuItem value="prescription">Prescription</MenuItem>
                  <MenuItem value="otc">Over the Counter</MenuItem>
                  <MenuItem value="supplement">Supplement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Posology"
                name="posology"
                value={formData.posology}
                onChange={handleChange}
                margin="normal"
                placeholder="e.g., Take 1 tablet daily with meals"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Source</InputLabel>
                <Select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  label="Source"
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="prescription">Prescription</MenuItem>
                  <MenuItem value="import">Imported</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.medicationName || loading}
          >
            {loading ? <CircularProgress size={24} /> : dialogMode === 'create' ? 'Create' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicationTest; 